import { useState, useEffect, useContext } from "react";
import moment from "moment";
import localforage from "localforage";
import { useIntl } from "react-intl";
import axios from "axios";
import Cookies from "js-cookie";

import { statuses } from "../components/MeteringPointInformation";
import AppConxt from "../AppContext";

async function updateToken(context) {
  try {
    await context.keycloak.updateToken(5);
  } catch(err) {
    context.keycloak.login();
  }
}

async function getCachedAlarms() {
  const alarms = await localforage.getItem('alarms');
  const alarmsUpdated = await localforage.getItem('alarmsUpdated');
  const period = await localforage.getItem('alarmsPeriod');
  const tagsList = await localforage.getItem('tagsList');
  
  const cacheIsComplete = Boolean(alarms) && Boolean(alarmsUpdated) && Boolean(period) && Boolean(tagsList)
  return cacheIsComplete ? {
    alarms, 
    alarmsUpdated, 
    period, 
    tagsList
  } : null
}

async function storeInCache(alarms) {
  await localforage.setItem('alarms', alarms.alarms);
  await localforage.setItem('alarmsUpdated', alarms.alarmsUpdated);
  await localforage.setItem('alarmsPeriod', alarms.period);
  await localforage.setItem('tagsList', alarms.tagsList);  
}

async function getLastServerRun(context) {
  await updateToken(context);
  const headers = {Authorization: "Bearer " + context.keycloak.token};
  const result = await axios.get(context.config.meterReadingsAPI + "/alarms/last_run", { headers });
  return result.data?.timestamp;
}

function getTags(alarms) {
  var tags = {};
  for (var alarm of alarms) {
    alarm.metering_point_status = (Boolean(alarm.status) &&
        statuses.includes(alarm.status[0])) ? alarm.status[0] : "";
    alarm.tags = alarm.tags || [];
    alarm.street_address = alarm.street_address || "";
    for (let tag of alarm.tags) {
      if (!(tag.key in tags)) {
        tags[tag.key] = new Set(tag.val);
      } else {
        tag.val.forEach(tags[tag.key].add, tags[tag.key]);
      }
    }
  }
  var tagsList = [];
  for (let [key,val] of Object.entries(tags)) {
    tagsList.push({"key": key, "val": Array.from(val)});
  }
  return tagsList;  
}

async function getServerAlarms(context, period) {
  await updateToken(context);
  var headers = { Authorization: "Bearer " + context.keycloak.token };
  var params = {
    include_from: moment().subtract(period, 'days').format('YYYY-MM-DD'),
    include_to: moment().subtract(1, 'days').format('YYYY-MM-DD'),
    interval: "daily"
  };
  const result = await axios.get(context.config.meterReadingsAPI + "/alarms", {
    headers,
    params
  });  
  return {
    alarms: result.data, 
    period,
    tagsList: getTags(result.data)
  };
}

function resetCurrentPage() {
  Cookies.set("k2_alarms_current_page", "" + 0, {
    expires: new Date(Date.now() + 1000000000000), sameSite: "lax",
  });
}


function useAlarms() {
  const context = useContext(AppConxt);
  const intl = useIntl();
  const [message, setMessage] = useState("");
  const [isServerRunning, setIsServerRunning] = useState(false);
  const [alarmsUpdated, setAlarmsUpdated] = useState(null);
  const [period, setPeriod] = useState("");
  const [alarms, setAlarms] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAlarms();
  }, []);

  async function fetchAlarms(newPeriod) {
    setIsLoading(true);
    try {
      let alarmState = await getCachedAlarms();
      const lastServerRun = await getLastServerRun(context);
      setAlarmsUpdated(lastServerRun)
      newPeriod = newPeriod || (alarmState && alarmState.period) || period;
      if (!alarmState || alarmState.period !== newPeriod || alarmState.alarmsUpdated !== lastServerRun) {
        setPeriod(newPeriod);
        alarmState = await getServerAlarms(context, newPeriod);
        alarmState.alarmsUpdated = lastServerRun;
        await storeInCache(alarmState);
        resetCurrentPage();
      }
      setPeriod(newPeriod);  
      setAlarms(alarmState.alarms);
      setTagsList(alarmState.tagsList);
    } catch(err) {
      setMessage(String(err))
    } finally {
      setIsLoading(false);
    }    
  }

  async function triggerServerRun() {
    await updateToken(context);
    const headers = { Authorization: "Bearer " + context.keycloak.token };
    setIsServerRunning(true);
    try {   
      setMessage(intl.formatMessage({id: "alarm.rebuilding"}));
      await axios.put(context.config.meterReadingsAPI + "/alarms", {}, {headers})
      setMessage(intl.formatMessage({ id: "alarm.rebuild_done" }))      
    } catch (error) {
      let errMsg = (error.response && error.response.status) ?
        error.response.status: "Skapandet av larmlistor tar lång tid. Vänta någon några minuter och hämta om larmen med vänstra knappen ovan";
      setMessage(errMsg)
    } finally {
      setIsServerRunning(false)
    }    
  }
  
  return {
    alarms, 
    tagsList, 
    period, 
    alarmsUpdated, 
    isLoading, 
    message, 
    isServerRunning, 
    setMessage, 
    fetchAlarms, 
    triggerServerRun
  }
}

export default useAlarms;